import { escapeRich, hasRichFormatting, isChoice, parseRich, questionTypeLabel, richToPlain, sectionRuns, type Definition, type Issue, type Question } from '@survey/core';
import { AlertTriangle, CornerDownRight, Eye, ListPlus, Monitor, Repeat, Shuffle, Smartphone, Tablet } from 'lucide-react';
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useI18n } from '../../i18n';
import { useStoredState } from '../../hooks/useStoredState';
import { usePref } from '../../hooks/usePref';
import { QuestionInput } from '../QuestionInput';
import { MediaEditor } from './MediaEditor';
import { QuestionMedia } from '../QuestionMedia';
import { RichEditor } from '../RichEditor';
import { alignStyle, RichText } from '../RichText';
import { Switch } from '../ui';
import { OptionList } from './TypeEditors';
import { TypePicker } from './TypePicker';
import { conditionPhrase } from './logicText';
import { changeType, logicFrom, randomizerOfSection, TYPE_ICONS } from './model';

interface Props {
  def: Definition;
  issues: Issue[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** The selected card is edited in place (text, code, type, required); without this it only previews. */
  canEdit?: boolean;
  onChangeQuestion?: (q: Question) => void;
  /** Renames a section, or names the questions that are not in one yet; `firstId` is the section's first question. */
  onRenameSection?: (firstId: string, title: string) => void;
  /** Switches the builder back to the list of titles or codes. */
  onMode?: (mode: 'edit' | 'preview') => void;
}

/** The name of a section, edited in place; the questions outside any section are named here to start one. */
function SectionName({ title, firstId, taken, onRename }: { title: string; firstId: string; taken: string[]; onRename: (firstId: string, title: string) => void }) {
  const { t } = useI18n();
  const [value, setValue] = useState(title);
  useEffect(() => setValue(title), [title]);
  const next = value.trim();
  const clash = next !== title && taken.includes(next);
  const commit = () => {
    if (!next || next === title || clash) return setValue(title);
    onRename(firstId, next);
  };
  return (
    <input
      className={`all-section-name${clash ? ' invalid' : ''}`}
      value={value}
      maxLength={120}
      placeholder={t('all.no_section')}
      aria-label={t('all.section_name')}
      aria-invalid={clash || undefined}
      title={clash ? t('flow.name_taken') : undefined}
      size={Math.max(8, Math.min(40, value.length + 2))}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setValue(title);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** The options of a choice question, edited on its card: text, image, remove and points. */
function InlineOptions({ question: q, onChange }: { question: Question; onChange: (q: Question) => void }) {
  const { t } = useI18n();
  const hasScores = (q.options ?? []).some((o) => typeof o.score === 'number');
  const [scoring, setScoring] = useState(hasScores);
  useEffect(() => setScoring(hasScores), [q.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (on: boolean) => {
    setScoring(on);
    if (!on && hasScores) onChange({ ...q, options: q.options?.map(({ score: _score, ...o }) => o) });
  };
  return (
    <div className="field all-q-options" onClick={(e) => e.stopPropagation()}>
      <div className="row gap-8">
        <span className="label grow">{t('qe.options')}</span>
        <Switch checked={scoring} onChange={toggle} label={t('qe.assign_points')} />
      </div>
      <OptionList question={q} kind="choice" onChange={onChange} scoring={scoring} />
    </div>
  );
}


/**
 * Every question of the questionnaire in one scrolling view, as a respondent
 * would see it, grouped under section markers. It is for scanning the whole
 * survey; clicking a question selects it and its settings open in the panel
 * under the question list.
 */
type Device = 'desktop' | 'tablet' | 'mobile';
const DEVICES: { id: Device; Icon: typeof Monitor }[] = [
  { id: 'desktop', Icon: Monitor },
  { id: 'tablet', Icon: Tablet },
  { id: 'mobile', Icon: Smartphone },
];

export function AllQuestions({ def, issues, selectedId, onSelect, canEdit = false, onChangeQuestion, onRenameSection, onMode }: Props) {
  const { t, locale } = useI18n();
  const [device, setDevice] = useStoredState<Device>('survey.builder.device', 'desktop');
  const [, setLabel] = usePref<'title' | 'code'>('survey.outline.label', 'title');
  const runs = useMemo(() => sectionRuns(def), [def]);
  const number = useMemo(() => new Map(def.questions.map((q, i) => [q.id, i + 1])), [def]);
  const issueIds = useMemo(() => new Set(issues.map((i) => i.questionId).filter(Boolean)), [issues]);
  const titled = runs.filter((r) => r.title);

  // A question picked in the list or the flow scrolls into view here.
  useEffect(() => {
    if (selectedId) document.getElementById(`all-q-${selectedId}`)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId]);

  const ref = (id: string) => t('flow.q_ref', { n: number.get(id) ?? 0 });

  const card = (q: Question) => {
    const Icon = TYPE_ICONS[q.type];
    const active = q.id === selectedId;
    // The selected card is the editor; its controls must not be inside a button role.
    const editing = active && canEdit && !!onChangeQuestion;
    const change = (patch: Partial<Question>) => onChangeQuestion?.({ ...q, ...patch });
    const textPatch = (field: 'title' | 'description', src: string): Partial<Question> => {
      const plain = richToPlain(src);
      const rich = hasRichFormatting(parseRich(src)) ? src : undefined;
      return field === 'title' ? { title: plain, titleRich: rich } : { description: plain || undefined, descriptionRich: rich };
    };
    const setText = (field: 'title' | 'description', src: string) => change(textPatch(field, src));
    const vi = q.visibleIf;
    const leads = logicFrom(def, q.id);
    // Respondents see options in their own random order; the builder shows the written order.
    const shown: Question = { ...q, randomizeOptions: false, randomizeRows: false };
    const onKey = (e: KeyboardEvent) => {
      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onSelect(q.id);
      }
    };
    return (
      <article
        key={q.id}
        id={`all-q-${q.id}`}
        className={`all-q${active ? ' active' : ''}`}
        role={editing ? undefined : 'button'}
        tabIndex={editing ? undefined : 0}
        aria-current={active || undefined}
        aria-label={editing ? undefined : t('all.edit_question', { n: number.get(q.id) ?? 0, title: q.title || t('common.untitled_question') })}
        onClick={() => onSelect(q.id)}
        onKeyDown={editing ? undefined : onKey}
      >
        <header className="all-q-meta">
          <span className="n">{number.get(q.id)}</span>
          {editing ? (
            <>
              <input
                className="all-q-code"
                value={q.questionCode ?? ''}
                maxLength={32}
                placeholder={t('qe.question_code_placeholder')}
                aria-label={t('qe.question_code')}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => change({ questionCode: e.target.value || undefined })}
              />
              <TypePicker className="all-q-type-picker" label={t('qe.type')} value={q.type} onChange={(type) => onChangeQuestion?.(changeType(q, type))} />
              {q.type !== 'descriptive' && (
                <button type="button" className={`pill req-toggle${q.required ? ' on' : ''}`} aria-pressed={q.required} onClick={(e) => { e.stopPropagation(); change({ required: !q.required }); }}>
                  {t('common.required')}
                </button>
              )}
            </>
          ) : (
            <>
              {q.questionCode && <span className="pill mono">{q.questionCode}</span>}
              <span className="all-q-type">
                <Icon /> {questionTypeLabel(q.type, locale)}
              </span>
              {q.required && q.type !== 'descriptive' && <span className="pill">{t('common.required')}</span>}
            </>
          )}
          {issueIds.has(q.id) && (
            <span className="pill orange" title={t('builder.needs_attention')}>
              <AlertTriangle /> {t('builder.needs_attention')}
            </span>
          )}
          <span className="grow" />
          {active && <span className="pill green">{t('all.editing')}</span>}
        </header>

        {editing ? (
          <>
            <RichEditor
              key={`title-${q.id}`}
              value={q.titleRich ?? escapeRich(q.title)}
              label={t(q.type === 'descriptive' ? 'qe.text' : 'qe.question')}
              placeholder={t(q.type === 'descriptive' ? 'rich.placeholder_text' : 'rich.placeholder_title')}
              onChange={(src) => setText('title', src)}
              align={q.textAlign}
              onAlign={(a, src) => change({ textAlign: a === 'left' ? undefined : a, ...(src !== undefined ? textPatch('title', src) : {}) })}
            />
            <RichEditor
              key={`desc-${q.id}`}
              compact
              value={q.descriptionRich ?? escapeRich(q.description ?? '')}
              label={t('qe.help_text')}
              placeholder={t('rich.placeholder_desc')}
              onChange={(src) => setText('description', src)}
              align={q.textAlign}
              onAlign={(a, src) => change({ textAlign: a === 'left' ? undefined : a, ...(src !== undefined ? textPatch('description', src) : {}) })}
            />
          </>
        ) : (
          <div style={alignStyle(q.textAlign)}>
            {q.titleRich ? (
              <div className="all-q-title">
                <RichText source={q.titleRich} inline={q.type !== 'descriptive'} />
              </div>
            ) : (
              <h3 className="all-q-title">{q.title || <span className="muted">{t('common.untitled_question')}</span>}</h3>
            )}
            {q.descriptionRich ? <RichText className="all-q-desc" source={q.descriptionRich} /> : q.description && <p className="all-q-desc">{q.description}</p>}
          </div>
        )}
        {editing ? (
          // The image or video is added, replaced and removed here, with the text it goes with.
          <div className="all-q-media" onClick={(e) => e.stopPropagation()}>
            <MediaEditor media={q.media} onChange={(media) => change({ media })} />
          </div>
        ) : (
          q.media && <QuestionMedia media={q.media} />
        )}
        {q.carryForward ? (
          <p className="all-q-note">
            <ListPlus /> {t(q.carryForward.mode === 'selected' ? 'flow.carries_selected' : 'flow.carries_not_selected', { q: ref(q.carryForward.questionId) })}
          </p>
        ) : editing && isChoice(q.type) && (q.options?.length ?? 0) > 0 ? (
          <InlineOptions question={q} onChange={(next) => onChangeQuestion?.(next)} />
        ) : (
          // The preview is for looking: nothing in it can be focused or changed.
          <div className="all-q-input" inert>
            <QuestionInput question={shown} value={undefined} seed="builder-preview" onChange={() => {}} />
          </div>
        )}

        {((vi && vi.conditions.length > 0) || q.jumpIf || leads.length > 0) && (
          <footer className="all-q-logic">
            {vi && vi.conditions.length > 0 && (
              <span className="flow-line">
                <Eye />
                <span>
                  {t('flow.shown_if')}{' '}
                  {vi.conditions.map((c, k) => (
                    <span key={k}>
                      {k > 0 && ` ${t(vi.match === 'any' ? 'flow.or' : 'flow.and')} `}
                      <strong>{ref(c.questionId)}</strong> {conditionPhrase(def.questions.find((x) => x.id === c.questionId), c.operator, c.value, t, locale)}
                    </span>
                  ))}
                </span>
              </span>
            )}
            {q.jumpIf?.targetQuestionId && (
              <span className="flow-line lead">
                <CornerDownRight />
                <span>
                  {t('flow.jump_if', { cond: conditionPhrase(q, q.jumpIf.operator, q.jumpIf.value, t, locale) })} <strong>{ref(q.jumpIf.targetQuestionId)}</strong>
                </span>
              </span>
            )}
            {leads.map((l, i) => (
              <span key={i} className="flow-line lead">
                {l.kind === 'shows' ? <CornerDownRight /> : <ListPlus />}
                <span>
                  {l.kind === 'shows'
                    ? t('flow.leads', { cond: l.conditions.map((c) => conditionPhrase(q, c.operator, c.value, t, locale)).join(` ${t(l.match === 'any' ? 'flow.or' : 'flow.and')} `) })
                    : t('flow.carries_to')}{' '}
                  <strong>{ref(l.target.id)}</strong>
                </span>
              </span>
            ))}
          </footer>
        )}
      </article>
    );
  };

  return (
    <div className="all-canvas">
      <div className="all-head">
        <h2>{t('all.title')}</h2>
        <span className="muted">{t('builder.total', { count: def.questions.length })}{titled.length > 0 && ` · ${t('builder.sections_total', { count: titled.length })}`}</span>
        <span className="grow" />
        <span className="muted small all-hint">{t('all.hint')}</span>
        {(def.settings.randomizers ?? []).some((r) => r.sections.length > 0) && <span className="small all-rz-note">{t('all.rz_note')}</span>}
        {onMode && (
          <div className="seg all-modes" role="group" aria-label={t('builder.outline_view')}>
            {(['title', 'code'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={false}
                onClick={() => {
                  setLabel(v);
                  onMode('edit');
                }}
              >
                {t(v === 'title' ? 'builder.outline_titles' : 'builder.outline_codes')}
              </button>
            ))}
            <button type="button" className="active" aria-pressed>
              {t('builder.outline_preview')}
            </button>
          </div>
        )}
        <div className="seg all-devices" role="group" aria-label={t('all.device')}>
          {DEVICES.map(({ id, Icon }) => (
            <button key={id} type="button" className={device === id ? 'active' : ''} aria-pressed={device === id} title={t(`all.device_${id}`)} aria-label={t(`all.device_${id}`)} onClick={() => setDevice(id)}>
              <Icon />
            </button>
          ))}
        </div>
      </div>
      <div className={`all-frame device-${device}`}>
      {runs.map((run, i) => {
        const rz = randomizerOfSection(def, run.title);
        const loop = run.title ? def.settings.sectionOptions?.[run.title]?.loopOver : undefined;
        const sectionNo = run.title ? titled.indexOf(run) + 1 : 0;
        return (
          <section key={`${i}:${run.title}`} className={`all-section${run.title ? '' : ' none'}`} aria-label={run.title || t('all.no_section')}>
            <div className="all-section-head">
              <span className="all-section-mark" aria-hidden />
              {run.title ? <span className="all-section-no">{t('all.section_n', { n: sectionNo })}</span> : null}
              {canEdit && onRenameSection ? (
                <SectionName title={run.title} firstId={run.questions[0]!.id} taken={titled.map((r) => r.title)} onRename={onRenameSection} />
              ) : (
                <strong className="all-section-title">{run.title || t('all.no_section')}</strong>
              )}
              <span className="muted">{t('builder.section_count', { count: run.questions.length })}</span>
              {rz && (
                <span className="pill blue rz" title={t('builder.in_randomizer', { present: rz.present, total: rz.sections.length })}>
                  <Shuffle /> {t('all.randomizer', { present: rz.present, total: rz.sections.length })}
                </span>
              )}
              {loop && (
                <span className="pill rz" title={t('flow.loops', { q: ref(loop) })}>
                  <Repeat /> {t('flow.loops', { q: ref(loop) })}
                </span>
              )}
              {!run.title && <span className="muted small">{t('all.no_section_hint')}</span>}
            </div>
            <div className="all-section-body">{run.questions.map(card)}</div>
          </section>
        );
      })}
      </div>
    </div>
  );
}
